// index.tsx
import React, { useEffect } from "react";
import ReactDOM from "react-dom/client";
import { ThemeProvider as EmotionThemeProvider } from "@emotion/react";
import emotionTheme from "./theme/components/theme";
import { ThemeProvider as CustomThemeProvider } from "./theme/components/ThemeProvider";
import RootRouter from "./routes";
import "./index.css";
import "./theme/styles/themes.css";
import "reactflow/dist/style.css";

import { Provider } from "react-redux";
import { store, persistor } from "./shared/redux/store/store";
import { PersistGate } from "redux-persist/integration/react";

import axios from "axios";

import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";

import { setUserProfile } from "./shared/redux/slices/userProfileSlice";
import { useAppDispatch } from "./shared/redux/hooks";
import { getDisplayName } from "@shared/components/utils/displayNameHelper";
import ResponsiveGate from "@shared/components/ResponsiveGate";
import { clearSelection, setTenant } from "@redux/slices/tenantSlice";
import Auth0RedirectHandler from "@shared/components/Auth0RedirectHandler";
import UserEditorHost from "@shared/components/UserEditorHost";

const auth0Domain = process.env.REACT_APP_AUTH0_DOMAIN || "";
const auth0ClientId = process.env.REACT_APP_AUTH0_CLIENT_ID || "";
const auth0Audience = process.env.REACT_APP_AUTH0_AUDIENCE || "";
const auth0RedirectUri =
  process.env.REACT_APP_REDIRECT_URI || window.location.origin;

// Base de la API y /v1
const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3002";
const API_V1_URL = `${API_BASE_URL}/v1`;

const container = document.getElementById("root");
if (!container) {
  throw new Error("Couldn't find the #root element in the HTML");
}
const root = ReactDOM.createRoot(container);

async function fetchUserProfile(token: string) {
  try {
    const response = await axios.post(
      `${API_V1_URL}/auth0/user-profile`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error al llamar /auth0/user-profile:", error);
    throw error;
  }
}


function LoadUserProfileOnMount() {
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const dispatch = useAppDispatch();
  const [hasFetchedProfile, setHasFetchedProfile] = React.useState(false);

  useEffect(() => {
    if (!isLoading && isAuthenticated && !hasFetchedProfile) {
      setHasFetchedProfile(true);
      (async () => {
        try {
          const token = await getAccessTokenSilently();
          const data = await fetchUserProfile(token);
          dispatch(setUserProfile(data));
          //           
          if (data.TenantType === "CUSTOMER") {
            //nuke any old partner/tenant
            dispatch(clearSelection());
            dispatch(setTenant({
              id:   data.tenantId,
              name: data.Tenant.nameDisplay,
              domain: "" 
            }));
          }
        } catch (err) {
          console.error("Error al obtener token y/o user-profile:", err);
        } finally {
        //Added to dynamically render the name of the host in the tab
        document.title =  `${getDisplayName()}`;
        }
      })();
    }
  }, [isLoading, isAuthenticated, hasFetchedProfile, getAccessTokenSilently, dispatch]);

  return null;
}

root.render(
  <EmotionThemeProvider theme={emotionTheme}>
    <CustomThemeProvider>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <Auth0Provider
            domain={auth0Domain}
            clientId={auth0ClientId}
            authorizationParams={{
              audience: auth0Audience,
              redirect_uri: auth0RedirectUri,
            }}
            cacheLocation="localstorage"
          >
            <ResponsiveGate>
              <RootRouter />
              <Auth0RedirectHandler />
              <LoadUserProfileOnMount />
              <UserEditorHost />
            </ResponsiveGate>
          </Auth0Provider>
        </PersistGate>
      </Provider>
    </CustomThemeProvider>
  </EmotionThemeProvider>
);
